import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react'
import { validateSignIn, validateSignUp, validateOtp, validateNewPassword, validateEmailOnly } from './authValidation'
import type { ValidationErrors } from './authValidation'
import { apiSignIn, apiSignUp, apiVerifyOtp, apiResendOtp, apiRequestPasswordReset, apiUpdatePassword, apiGoogleSignIn } from '../../api/auth/authApi'
import { apiGetUserProfile, notifyProfileUpdated } from '../../api/profile/userProfileApi'
import { API_CONFIG } from '../../api/config'
import type { ApiResponse } from '../../api/auth/authApi'
import {
  applyTempDemoSession,
  isTempDemoLogin,
} from './tempAuth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onLoginSuccess: (email: string) => void
}

type AuthStep = 'signIn' | 'signUp' | 'forgotPassword' | 'otp' | 'newPassword' | 'success'
type OtpPurpose = 'signUp' | 'passwordReset'

export default function AuthModal({ isOpen, onClose, onLoginSuccess }: AuthModalProps) {
  const [step, setStep] = useState<AuthStep>('signIn')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null)

  // Sign In Form States
  const [signInEmail, setSignInEmail] = useState('')
  const [signInPassword, setSignInPassword] = useState('')
  const [showSignInPassword, setShowSignInPassword] = useState(false)
  const [signInRemember, setSignInRemember] = useState(false)

  // Forgot Password Form State
  const [forgotEmail, setForgotEmail] = useState('')
  const [otpPurpose, setOtpPurpose] = useState<OtpPurpose>('signUp')

  // Sign Up Form States
  const [signUpEmail, setSignUpEmail] = useState('')
  const [signUpPhone, setSignUpPhone] = useState('')
  const [signUpRemember, setSignUpRemember] = useState(false)

  // OTP Form States
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''))
  const [resendTimer, setResendTimer] = useState(105)
  const [canResend, setCanResend] = useState(false)
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([])

  // New Password Form States
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [googlePrompting, setGooglePrompting] = useState(false)
  const googleButtonRef = useRef<HTMLButtonElement | null>(null)
  const [googleFlow, setGoogleFlow] = useState(false)

  const navigate = useNavigate()
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  // Load Google Identity Services script once
  useEffect(() => {
    if (typeof document === 'undefined') return

    const scriptId = 'google-identity-service'
    if (document.getElementById(scriptId)) {
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.id = scriptId

    document.body.appendChild(script)
  }, [])

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Small timeout to prevent layout jump while modal is closing
      const timer = setTimeout(() => {
        setStep('signIn')
        setErrors({})
        setApiResponse(null)
        setSignInEmail('')
        setSignInPassword('')
        setForgotEmail('')
        setOtpPurpose('signUp')
        setSignUpEmail('')
        setSignUpPhone('')
        setOtpValues(Array(6).fill(''))
        setNewPassword('')
        setConfirmPassword('')
        setResendTimer(105)
        setCanResend(false)
        setGoogleFlow(false)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Reset/Start OTP timer when entering the OTP step
  useEffect(() => {
    if (step === 'otp') {
      setResendTimer(105)
      setCanResend(false)
      const focusTimer = setTimeout(() => otpInputRefs.current[0]?.focus(), 0)
      return () => clearTimeout(focusTimer)
    }
  }, [step])

  // OTP Countdown Timer Interval
  useEffect(() => {
    let interval: any
    if (step === 'otp' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [step, resendTimer])

  const otpEmail = otpPurpose === 'passwordReset' ? forgotEmail : signUpEmail
  const formattedResendTimer = `${String(Math.floor(resendTimer / 60)).padStart(2, '0')}:${String(resendTimer % 60).padStart(2, '0')}`

  if (!isOpen) return null

  // Focus helper for OTP inputs
  const handleOtpChange = (index: number, value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, '')
    if (!cleanValue) {
      const newOtp = [...otpValues]
      newOtp[index] = ''
      setOtpValues(newOtp)
      return
    }

    const newOtp = [...otpValues]
    newOtp[index] = cleanValue[cleanValue.length - 1]
    setOtpValues(newOtp)

    if (index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otpValues[index] === '') {
        if (index > 0) {
          const newOtp = [...otpValues]
          newOtp[index - 1] = ''
          setOtpValues(newOtp)
          otpInputRefs.current[index - 1]?.focus()
        }
      } else {
        const newOtp = [...otpValues]
        newOtp[index] = ''
        setOtpValues(newOtp)
      }
      e.preventDefault()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').trim()
    if (!/^\d+$/.test(pasteData)) return

    const pasteDigits = pasteData.slice(0, 6).split('')
    const newOtp = [...otpValues]
    for (let i = 0; i < 6; i++) {
      if (pasteDigits[i]) {
        newOtp[i] = pasteDigits[i]
      }
    }
    setOtpValues(newOtp)
    const focusIndex = Math.min(pasteDigits.length, 5)
    otpInputRefs.current[focusIndex]?.focus()
  }

  const handleResendOtpClick = async () => {
    if (!canResend) return
    setErrors({})
    setApiResponse(null)
    setLoading(true)
    try {
      const res = await apiResendOtp(otpEmail)
      setApiResponse(res)
      if (res.success) {
        setResendTimer(105)
        setCanResend(false)
        setOtpValues(Array(6).fill(''))
        setTimeout(() => otpInputRefs.current[0]?.focus(), 0)
        setApiResponse(null)
      } else {
        setErrors({ otp: res.warning || res.message })
        setApiResponse(null)
      }
    } catch (err) {
      setErrors({ otp: 'Resend code failed.' })
    } finally {
      setLoading(false)
    }
  }


  const openForgotPassword = () => {
    setForgotEmail(step === 'signUp' ? signUpEmail : signInEmail)
    setErrors({})
    setApiResponse(null)
    setStep('forgotPassword')
  }

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setApiResponse(null)

    const clientErrors = validateEmailOnly(forgotEmail)
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors)
      return
    }

    setLoading(true)
    try {
      const res = await apiRequestPasswordReset(forgotEmail.trim())
      setApiResponse(res)
      if (res.success) {
        setOtpPurpose('passwordReset')
        setOtpValues(Array(6).fill(''))
        setResendTimer(105)
        setCanResend(false)
        setStep('otp')
        setErrors({})
      } else {
        setErrors({ email: res.warning || res.message })
      }
    } catch (err) {
      setErrors({ email: 'Unable to send the reset code. Please try again.' })
    } finally {
      setLoading(false)
    }
  }


  const completeLocalLogin = (email: string, message: string) => {
    applyTempDemoSession(email)
    setApiResponse({ success: true, message })
    onLoginSuccess(email)
    setTimeout(() => {
      onClose()
    }, 800)
  }

  // Submit Handlers
  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setApiResponse(null)

    const clientErrors = validateSignIn(signInEmail, signInPassword)
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors)
      return
    }

    // Temporary demo login — skips the API entirely; real accounts still use authApi below.
    if (isTempDemoLogin(signInEmail, signInPassword)) {
      setLoading(true)
      completeLocalLogin(signInEmail.trim().toLowerCase(), 'Login successful!')
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const res = await apiSignIn(signInEmail, signInPassword)
      setApiResponse(res)
      if (res.success) {
        await apiGetUserProfile()
        // Logged in successfully
        onLoginSuccess(signInEmail)
        setTimeout(() => {
          onClose()
        }, 1000)
      } else {
        setErrors({
          email: res.message,
          password: res.warning
        })
      }
    } catch (err) {
      setErrors({ email: 'Network connection failed.' })
    } finally {
      setLoading(false)
    }
  }


  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setApiResponse(null)

    const clientErrors = validateSignUp(signUpEmail, signUpPhone)
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors)
      return
    }

    setLoading(true)
    try {
      const res = await apiSignUp(signUpEmail, signUpPhone)
      setApiResponse(res)
      if (res.success) {
         localStorage.setItem('salzburg_profile_details', JSON.stringify({
          name: signUpEmail.split('@')[0],
          email: signUpEmail.trim(),
          phone: signUpPhone.trim(),
        }))
        setOtpPurpose('signUp')
        // Go to OTP validation screen next
        setTimeout(() => {
          setStep('otp')
          setErrors({})
          setApiResponse(null)
        }, 1000)
      } else {
        setErrors({
          email: res.message,
          phone: res.warning
        })
      }
    } catch (err) {
      setErrors({ email: 'Registration failed.' })
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleTokenResponse = async (accessToken: string) => {
    if (!accessToken) {
      setErrors({ email: 'Google sign-in failed. No token received.' })
      setGooglePrompting(false)
      return
    }

    setLoading(true)
    setErrors({})
    setApiResponse(null)

    try {
      // 1. Fetch user profile from Google's UserInfo API
      let googleProfile: any = null
      try {
        const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        if (userInfoRes.ok) {
          googleProfile = await userInfoRes.json()
        }
      } catch (e) {
        console.warn('Could not fetch Google userinfo directly', e)
      }

      // 2. Fetch phone number from Google People API if accessible
      let googlePhone = ''
      try {
        const peopleRes = await fetch(
          'https://people.googleapis.com/v1/people/me?personFields=phoneNumbers,names,emailAddresses',
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        )
        if (peopleRes.ok) {
          const peopleData = await peopleRes.json()
          const primaryPhone =
            peopleData.phoneNumbers?.[0]?.value ||
            peopleData.phoneNumbers?.[0]?.canonicalForm
          if (primaryPhone) {
            googlePhone = primaryPhone
          }
        }
      } catch (e) {
        // People API may be restricted or have no phone, handled gracefully
      }

      // 3. Authenticate with backend API
      const res = await apiGoogleSignIn(accessToken, 'access_token')

      const email =
        res.data?.user?.email || googleProfile?.email || ''
      const name =
        res.data?.user?.name ||
        res.data?.user?.full_name ||
        [res.data?.user?.first_name, res.data?.user?.last_name].filter(Boolean).join(' ') ||
        googleProfile?.name ||
        [googleProfile?.given_name, googleProfile?.family_name].filter(Boolean).join(' ') ||
        (email ? email.split('@')[0] : 'Google User')
      const phone =
        res.data?.user?.phone ||
        res.data?.user?.phone_number ||
        res.data?.user?.phoneNumber ||
        googlePhone ||
        ''
      const avatar =
        res.data?.user?.avatar ||
        res.data?.user?.picture ||
        googleProfile?.picture ||
        ''

      if ((res.success && res.data?.token) || (email && googleProfile)) {
        const sessionToken = res.data?.token || accessToken
        localStorage.setItem('auth_token', sessionToken)
        localStorage.setItem('user_email', email)
        localStorage.setItem(
          'user_data',
          JSON.stringify({
            name,
            email,
            phone,
            avatar,
            auth_provider: 'google',
          }),
        )
        localStorage.setItem(
          'salzburg_profile_details',
          JSON.stringify({
            name,
            email,
            phone,
            avatar,
            has_password: false,
          }),
        )
        notifyProfileUpdated()

        setApiResponse({
          success: true,
          message: `Signed in successfully as ${name}`,
        })
        onLoginSuccess(email)
        setTimeout(() => {
          onClose()
        }, 800)
      } else if (res.success) {
        setErrors({ email: 'Google sign-in succeeded but no token was returned.' })
      } else {
        setErrors({ email: res.message, password: res.warning })
      }
    } catch (err) {
      setErrors({ email: 'Google sign-in failed. Please try again.' })
    } finally {
      setLoading(false)
      setGooglePrompting(false)
    }
  }

  const handleSimulatedGoogleSignIn = () => {
    setLoading(true)
    setErrors({})
    setApiResponse(null)

    const googleUser = {
      name: 'Alexander Wright',
      email: 'alexander.wright.google@gmail.com',
      phone: '+43 664 1234567',
      avatar: '/assets/dashboard/profile/profile-avatar.png',
      auth_provider: 'google',
    }

    localStorage.setItem('auth_token', 'mock_google_oauth_token_' + Date.now())
    localStorage.setItem('user_email', googleUser.email)
    localStorage.setItem('user_data', JSON.stringify(googleUser))
    localStorage.setItem(
      'salzburg_profile_details',
      JSON.stringify({
        ...googleUser,
        has_password: false,
      }),
    )
    notifyProfileUpdated()

    setApiResponse({
      success: true,
      message: `Signed in as ${googleUser.name} (Google Account)`,
    })
    onLoginSuccess(googleUser.email)
    setTimeout(() => {
      setLoading(false)
      onClose()
    }, 800)
  }

  const handleGoogleSignIn = () => {
    setErrors({})
    setApiResponse(null)

    const google = (window as any).google
    if (!GOOGLE_CLIENT_ID || !google?.accounts?.oauth2) {
      // If Google OAuth script or client ID is not available, provide demo Google account sign-in
      handleSimulatedGoogleSignIn()
      return
    }

    if (googlePrompting) {
      return
    }

    setGooglePrompting(true)
    try {
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile https://www.googleapis.com/auth/user.phonenumbers.read',
        callback: (response: any) => {
          if (response.error) {
            setGooglePrompting(false)
            setErrors({ email: 'Google sign-in was cancelled or origin is not permitted in Google Cloud Console.' })
            return
          }
          handleGoogleTokenResponse(response.access_token)
        },
        error_callback: (err: any) => {
          setGooglePrompting(false)
          setErrors({ email: err?.message || 'Google sign-in popup failed to open.' })
        },
      })

      tokenClient.requestAccessToken()
    } catch (error) {
      setGooglePrompting(false)
      setErrors({ email: 'Unable to start Google sign-in. Please try again.' })
    }
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setApiResponse(null)

    if (canResend) {
      setErrors({ otp: 'This code has expired. Please request a new code.' })
      return
    }

    const clientErrors = validateOtp(otpValues)
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors)
      return
    }

    setLoading(true)
    try {
      const res = await apiVerifyOtp(otpEmail, otpValues.join(''))
      setApiResponse(res)
      if (res.success) {
        if (googleFlow && res.data?.token) {
          onLoginSuccess(res.data.user?.email || otpEmail)
          setTimeout(() => onClose(), 1000)
        } else {
          setStep('newPassword')
          setErrors({})
        }
      } else {
        setErrors({ otp: res.warning || res.message })
      }
    } catch (err) {
      setErrors({ otp: 'Verification failed. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setApiResponse(null)

    const clientErrors = validateNewPassword(newPassword, confirmPassword)
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors)
      return
    }

    setLoading(true)
    try {
      const res = await apiUpdatePassword(otpEmail, newPassword)
      setApiResponse(res)
      if (res.success) {
        setStep('success')
        setErrors({})
      } else {
        setErrors({
          password: res.message,
          confirmPassword: res.warning
        })
      }
    } catch (err) {
      setErrors({ password: 'Password update failed. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-all duration-300">
      {/* Outer Modal Box */}
      <div 
        className={`relative flex w-full flex-col rounded-[28px] bg-white transition-all duration-300 animate-in fade-in zoom-in-95 duration-200 shadow-[0_8px_32px_rgba(0,0,0,0.14)] ${
          step === 'signIn' || step === 'signUp' ? 'max-w-[940px] p-3 md:flex-row' : 'max-w-[480px] p-7 sm:p-9'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Modal Button */}
        {step === 'newPassword' || step === 'otp' || step === 'forgotPassword' ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close authentication modal"
            className="absolute right-6 top-6 z-50 flex h-8 w-8 items-center justify-center rounded-full border border-[#96AEFC]/70 text-[#3B82F6] transition-colors hover:bg-blue-50"
          >
            <X className="h-4 w-4 stroke-[2.5]" />
          </button>
        ) : (step === 'signIn' || step === 'signUp') ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close authentication modal"
            className="absolute right-6 top-6 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm border border-gray-100 hover:text-gray-900 transition-colors"
          >
            <X className="h-4 w-4 stroke-[2.2]" />
          </button>
        ) : null}

        {/* LEFT / MAIN PANEL */}
        <div className={`flex-1 flex flex-col justify-center ${step === 'signIn' || step === 'signUp' ? 'px-6 py-8 sm:px-10' : ''}`}>
          
          {/* Header row (Titles) */}
          {step === 'signIn' && (
            <div>
              <h2 className="font-host text-[28px] font-bold leading-tight tracking-tight text-[#111111]">
                Sign In
              </h2>
            </div>
          )}

          {step === 'signUp' && (
            <div>
              <h2 className="font-host text-[28px] font-bold leading-tight tracking-tight text-[#111111]">
                Create Account
              </h2>
            </div>
          )}

          {step === 'forgotPassword' && (
            <div className="pr-8">
              <h2 className="font-host text-[22px] sm:text-[24px] font-bold leading-tight tracking-tight text-[#111111]">
                Forgot Your Password?
              </h2>
              <p className="mt-1.5 font-poppins text-[13px] sm:text-[14px] leading-relaxed text-[#6E6E6E]">
                No worries! Enter your registered email address, and we'll send you a link to reset your password.
              </p>
            </div>
          )}

          {step === 'otp' && (
            <div className="pr-8">
              <h2 className="font-host text-[24px] sm:text-[26px] font-bold leading-tight tracking-tight text-[#111111]">
                OTP
              </h2>
              <p className="mt-1.5 font-poppins text-[13px] sm:text-[14px] leading-relaxed text-[#6E6E6E]">
                Check Your Email To Get One Time Password & Enter Here
              </p>
            </div>
          )}

          {step === 'newPassword' && (
            <div className="pr-8">
              <h2 className="font-host text-[22px] sm:text-[24px] font-bold leading-tight tracking-tight text-[#111111]">
                Create a New Password
              </h2>
              <p className="mt-1.5 font-poppins text-[13px] sm:text-[14px] leading-relaxed text-[#6E6E6E]">
                Create a strong password to keep your Salzburg Cab account secure.
              </p>
            </div>
          )}

          {/* Backend Warning Alerts */}
          {apiResponse && !apiResponse.success && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-red-50 p-4 border border-red-100 text-red-700 animate-in slide-in-from-top-1 duration-200">
              <AlertCircle className="mt-0.5 h-[17px] w-[17px] shrink-0" />
              <div>
                <p className="font-poppins text-[13px] font-semibold">{apiResponse.message}</p>
                {apiResponse.warning && (
                  <p className="mt-1 font-poppins text-[11px] leading-relaxed opacity-90">{apiResponse.warning}</p>
                )}
              </div>
            </div>
          )}

          {/* Backend Success Alerts */}
          {apiResponse && apiResponse.success && step !== 'success' && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-green-50 p-4 border border-green-100 text-green-700 animate-in slide-in-from-top-1 duration-200">
              <Check className="mt-0.5 h-[17px] w-[17px] shrink-0 rounded-full bg-green-600 p-0.5 text-white" />
              <div>
                <p className="font-poppins text-[13px] font-semibold">{apiResponse.message}</p>
              </div>
            </div>
          )}

          {/* Forms Section */}
          <div className="mt-6 flex-1">
            
            {/* SIGN IN FORM */}
            {step === 'signIn' && (
              <form onSubmit={handleSignInSubmit} className="space-y-4">
                <div>
                  <label className="block font-poppins text-[13px] font-semibold text-[#111111]">
                    Email Address
                  </label>
                  <input
                    type="text"
                    value={signInEmail}
                    onChange={(e) => setSignInEmail(e.target.value)}
                    placeholder="Email Address"
                    className={`mt-2 h-[52px] w-full rounded-[14px] border bg-white px-4 font-poppins text-[14px] text-black outline-none transition-colors ${
                      errors.email ? 'border-red-400 focus:border-red-500' : 'border-[#E2E8F0] focus:border-brand-orange'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 font-poppins text-[11px] text-red-500 font-medium">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block font-poppins text-[13px] font-semibold text-[#111111]">
                    New Password
                  </label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <Lock className="h-[18px] w-[18px]" />
                    </span>
                    <input
                      type={showSignInPassword ? 'text' : 'password'}
                      value={signInPassword}
                      onChange={(e) => setSignInPassword(e.target.value)}
                      placeholder="Enter your new password"
                      className={`h-[52px] w-full rounded-[14px] border bg-white pl-12 pr-12 font-poppins text-[14px] text-black outline-none transition-colors ${
                        errors.password ? 'border-red-400 focus:border-red-500' : 'border-[#E2E8F0] focus:border-brand-orange'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignInPassword(!showSignInPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSignInPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 font-poppins text-[11px] text-red-500 font-medium">{errors.password}</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signInRemember}
                      onChange={(e) => setSignInRemember(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                    />
                    <span className="font-poppins text-[12px] font-medium text-[#111111]">
                      Remember Me
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 flex h-[52px] w-full items-center justify-center rounded-[14px] bg-[#FF7A29] font-poppins text-[15px] font-semibold text-white transition-colors hover:bg-[#E56E25] active:scale-[0.99] disabled:opacity-50"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>

                <div className="flex items-center justify-between pt-4 font-poppins text-[12px]">
                  <p className="text-gray-500">
                    Don’t have an account ?{' '}
                    <button
                      type="button"
                      onClick={() => setStep('signUp')}
                      className="font-semibold text-[#3B82F6] underline"
                    >
                      Sign up
                    </button>
                  </p>
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="font-medium text-gray-800 hover:underline"
                  >
                    Forgot Password
                  </button>
                </div>

                <div className="pt-5 flex flex-col items-center">
                  <div className="relative flex w-full items-center justify-center">
                    <div className="w-full border-t border-gray-200"></div>
                    <span className="absolute bg-white px-3 font-poppins text-[12px] font-medium text-gray-500">
                      Or continue with
                    </span>
                  </div>
                  <div className="mt-4 w-full">
                    <button
                      ref={googleButtonRef}
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={loading || googlePrompting}
                      className="flex h-[46px] w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white font-poppins text-[14px] font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.99] disabled:opacity-50"
                    >
                      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.9h6.6c-.28 1.5-1.11 2.76-2.39 3.62v3h3.86c2.26-2.09 3.56-5.16 3.56-8.75Z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A11.97 11.97 0 0 0 12 24Z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.27 14.29a7.18 7.18 0 0 1 0-4.58V6.6H1.29a11.94 11.94 0 0 0 0 10.8l3.98-3.11Z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.96 11.96 0 0 0 1.29 6.6l3.98 3.1c.95-2.85 3.6-4.95 6.73-4.95Z"
                        />
                      </svg>
                      <span>Continue with Google</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* FORGOT PASSWORD FORM */}
            {step === 'forgotPassword' && (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                <div>
                  <label className="block font-poppins text-[14px] font-semibold text-[#111111]">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    className={`mt-2 h-[54px] w-full rounded-[16px] border bg-white px-4 font-poppins text-[14px] text-black outline-none transition-colors ${
                      errors.email ? 'border-red-400 focus:border-red-500' : 'border-[#E2E8F0] focus:border-brand-orange'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1.5 font-poppins text-[11px] font-medium text-red-500">{errors.email}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('signIn')
                      setErrors({})
                      setApiResponse(null)
                    }}
                    className="flex h-[54px] items-center justify-center rounded-[16px] border border-[#E2E8F0] bg-white font-poppins text-[15px] font-semibold text-[#111111] transition-colors hover:bg-gray-50 active:scale-[0.99]"
                  >
                    Back to Sign In
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex h-[54px] items-center justify-center rounded-[16px] bg-[#FF6420] font-poppins text-[15px] font-semibold text-white transition-colors hover:bg-[#E85514] active:scale-[0.99] disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            )}

            {/* CREATE ACCOUNT (SIGN UP) FORM */}
            {step === 'signUp' && (
              <form onSubmit={handleSignUpSubmit} className="space-y-4">
                <div>
                  <label className="block font-poppins text-[13px] font-semibold text-[#111111]">
                    Email Address
                  </label>
                  <input
                    type="text"
                    value={signUpEmail}
                    onChange={(e) => setSignUpEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`mt-2 h-[52px] w-full rounded-[14px] border bg-white px-4 font-poppins text-[14px] text-black outline-none transition-colors ${
                      errors.email ? 'border-red-400 focus:border-red-500' : 'border-[#E2E8F0] focus:border-brand-orange'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 font-poppins text-[11px] text-red-500 font-medium">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block font-poppins text-[13px] font-semibold text-[#111111]">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    value={signUpPhone}
                    onChange={(e) => setSignUpPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    className={`mt-2 h-[52px] w-full rounded-[14px] border bg-white px-4 font-poppins text-[14px] text-black outline-none transition-colors ${
                      errors.phone ? 'border-red-400 focus:border-red-500' : 'border-[#E2E8F0] focus:border-brand-orange'
                    }`}
                  />
                  {errors.phone && (
                    <p className="mt-1 font-poppins text-[11px] text-red-500 font-medium">{errors.phone}</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signUpRemember}
                      onChange={(e) => setSignUpRemember(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                    />
                    <span className="font-poppins text-[12px] font-medium text-[#111111]">
                      Remember Me
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 flex h-[52px] w-full items-center justify-center rounded-[14px] bg-[#FF7A29] font-poppins text-[15px] font-semibold text-white transition-colors hover:bg-[#E56E25] active:scale-[0.99] disabled:opacity-50"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                <div className="flex items-center justify-between pt-4 font-poppins text-[12px]">
                  <p className="text-gray-500">
                    Already have an account ?{' '}
                    <button
                      type="button"
                      onClick={() => setStep('signIn')}
                      className="font-semibold text-[#3B82F6] underline"
                    >
                      Sign in
                    </button>
                  </p>
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="font-medium text-gray-800 hover:underline"
                  >
                    Forgot Password
                  </button>
                </div>

                <div className="pt-5 flex flex-col items-center">
                  <div className="relative flex w-full items-center justify-center">
                    <div className="w-full border-t border-gray-200"></div>
                    <span className="absolute bg-white px-3 font-poppins text-[12px] font-medium text-gray-500">
                      Or continue with
                    </span>
                  </div>
                  <div className="mt-4 w-full">
                    <button
                      ref={googleButtonRef}
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={loading || googlePrompting}
                      className="flex h-[46px] w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white font-poppins text-[14px] font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.99] disabled:opacity-50"
                    >
                      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.9h6.6c-.28 1.5-1.11 2.76-2.39 3.62v3h3.86c2.26-2.09 3.56-5.16 3.56-8.75Z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A11.97 11.97 0 0 0 12 24Z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.27 14.29a7.18 7.18 0 0 1 0-4.58V6.6H1.29a11.94 11.94 0 0 0 0 10.8l3.98-3.11Z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.96 11.96 0 0 0 1.29 6.6l3.98 3.1c.95-2.85 3.6-4.95 6.73-4.95Z"
                        />
                      </svg>
                      <span>Continue with Google</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* OTP VERIFICATION FORM (SCREEN 2) */}
            {step === 'otp' && (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="flex items-center justify-between gap-2 sm:gap-2.5 pt-1">
                  {otpValues.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      ref={(el) => {
                        otpInputRefs.current[idx] = el
                      }}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      onPaste={handleOtpPaste}
                      className={`h-[56px] sm:h-[60px] w-[48px] sm:w-[54px] rounded-[16px] border bg-white text-center font-poppins text-[20px] font-bold text-black outline-none transition-all focus:border-[#FF6420] focus:ring-1 focus:ring-[#FF6420] ${
                        errors.otp ? 'border-red-400' : 'border-[#D1D5DB]'
                      }`}
                    />
                  ))}
                </div>
                {errors.otp && (
                  <p className="mt-1 text-center font-poppins text-[11px] text-red-500 font-medium">{errors.otp}</p>
                )}

                <div className="pt-3 font-poppins text-[14px] sm:text-[15px] font-semibold text-[#111111]">
                  {canResend ? (
                    <span>
                      Didn't receive the code?{' '}
                      <button
                        type="button"
                        onClick={handleResendOtpClick}
                        disabled={loading}
                        className="font-semibold text-[#FF6420] hover:underline disabled:opacity-50"
                      >
                        {loading ? 'Sending...' : 'Resend Code'}
                      </button>
                    </span>
                  ) : (
                    <span>Code expires in: {formattedResendTimer}</span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 flex h-[54px] sm:h-[56px] w-full items-center justify-center rounded-[16px] bg-[#FF6420] font-poppins text-[16px] font-semibold text-white transition-all hover:bg-[#E85514] active:scale-[0.99] disabled:opacity-50 shadow-sm"
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </form>
            )}

            {/* CREATE A NEW PASSWORD FORM (SCREEN 1) */}
            {step === 'newPassword' && (
              <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
                <div>
                  <label className="block font-poppins text-[14px] font-semibold text-[#111111]">
                    New Password
                  </label>
                  <div className={`mt-2 flex h-[54px] sm:h-[56px] w-full items-center rounded-[16px] border bg-white px-4 transition-all focus-within:border-[#FF6420] focus-within:ring-1 focus-within:ring-[#FF6420] ${
                    errors.password ? 'border-red-400' : 'border-[#E2E8F0]'
                  }`}>
                    <span className="text-[#8A8F98]">
                      <Lock className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password"
                      className="h-full w-full bg-transparent px-3 font-poppins text-[14px] text-black placeholder-[#9CA3AF] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-[#8A8F98] hover:text-[#4B5563] p-1 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5 strokeWidth={1.8}" /> : <Eye className="h-5 w-5 strokeWidth={1.8}" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 font-poppins text-[11px] font-medium text-red-500">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block font-poppins text-[14px] font-semibold text-[#111111]">
                    Confirm Password
                  </label>
                  <div className={`mt-2 flex h-[54px] sm:h-[56px] w-full items-center rounded-[16px] border bg-white px-4 transition-all focus-within:border-[#FF6420] focus-within:ring-1 focus-within:ring-[#FF6420] ${
                    errors.confirmPassword ? 'border-red-400' : 'border-[#E2E8F0]'
                  }`}>
                    <span className="text-[#8A8F98]">
                      <Lock className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      className="h-full w-full bg-transparent px-3 font-poppins text-[14px] text-black placeholder-[#9CA3AF] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-[#8A8F98] hover:text-[#4B5563] p-1 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5 strokeWidth={1.8}" /> : <Eye className="h-5 w-5 strokeWidth={1.8}" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1.5 font-poppins text-[11px] font-medium text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 flex h-[54px] sm:h-[56px] w-full items-center justify-center rounded-[16px] bg-[#FF6420] font-poppins text-[16px] font-semibold text-white transition-all hover:bg-[#E85514] active:scale-[0.99] disabled:opacity-50 shadow-sm"
                >
                  {loading ? 'Saving...' : 'Create Password'}
                </button>
              </form>
            )}

            {/* SUCCESS STATUS FORM (SCREEN 3) */}
            {step === 'success' && (
              <div className="flex flex-col items-center py-2 text-center">
                {/* Scalloped Starburst Checkmark Badge */}
                <div className="flex h-14 w-14 items-center justify-center">
                  <svg width="56" height="56" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                    <path
                      d="M24 2.5L27.2 6.8L32.4 5.2L34.1 10.3L39.4 10.7L39.3 16.1L43.8 18.5L41.9 23.5L44.8 27.5L41.3 31.2L42.5 36.5L37.2 38.3L36.4 43.7L31.1 43.4L28.3 47.9L24 44.5L19.7 47.9L16.9 43.4L11.6 43.7L10.8 38.3L5.5 36.5L6.7 31.2L3.2 27.5L6.1 23.5L4.2 18.5L8.7 16.1L8.6 10.7L13.9 10.3L15.6 5.2L20.8 6.8L24 2.5Z"
                      fill="#0B0B0B"
                    />
                    <path
                      d="M16.5 24.5L21.5 29.5L31.5 18.5"
                      stroke="white"
                      strokeWidth="3.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <h2 className="mt-4 font-host text-[22px] sm:text-[24px] font-bold leading-tight tracking-tight text-[#111111]">
                  Password Created Successfully!
                </h2>
                
                <p className="mt-2 font-poppins text-[14px] sm:text-[15px] leading-relaxed text-[#6E6E6E]">
                  Your password has been updated successfully.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setSignInEmail(otpEmail)
                    setSignInPassword('')
                    setNewPassword('')
                    setConfirmPassword('')
                    setOtpValues(Array(6).fill(''))
                    setErrors({})
                    setApiResponse(null)
                    setStep('signIn')
                  }}
                  className="mt-7 flex h-[54px] sm:h-[56px] w-full items-center justify-center rounded-[16px] bg-[#FF6420] font-poppins text-[16px] font-semibold text-white transition-all hover:bg-[#E85514] active:scale-[0.99] shadow-sm"
                >
                  Continue to sign In
                </button>
              </div>
            )}

          </div>
        </div>

        {/* RIGHT PANEL: Custom Salzburg Cab Teal-Blue design */}
        {(step === 'signIn' || step === 'signUp') && (
          <div className="relative hidden w-[400px] bg-gradient-to-tr from-[#b2dae5] via-[#7faec1] to-[#4b8da3] p-8 md:flex md:flex-col md:justify-between rounded-[24px] overflow-hidden">
            {/* Semicircle PNG background shape overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-[url('/assets/images/Ellipse 80.png')] bg-cover bg-top opacity-10 pointer-events-none" />

            <div>
              {/* Logo block */}
              <div className="flex flex-col">
                <img
                  src="/assets/images/logo.png"
                  alt="Salzburg Cab Logo"
                  className="h-11 w-auto object-contain self-start"
                />
                <span className="mt-1 font-poppins text-[9px] font-semibold uppercase tracking-wider text-black/50 leading-none">
                  Range of vehicle sizes to suit your needs
                </span>
              </div>

              {/* Title & description */}
              <h3 className="mt-10 font-host text-[26px] font-bold leading-tight text-slate-800 tracking-tight">
                Create Your Salzburg <br /> Cab Account
              </h3>
              
              <p className="mt-4 font-poppins text-[13px] font-medium leading-relaxed text-slate-700">
                Join Salzburg Cab to book faster, manage your trips, and enjoy a seamless travel experience across Austria and Europe.
              </p>
            </div>

            {/* Visual Salzburg Cab Fleet Vehicle */}
            <div className="relative mt-4 flex justify-center">
              <img
                src="/assets/images/fleet-passat.png"
                alt="Salzburg Cab Fleet Vehicle"
                className="max-h-[185px] w-[115%] max-w-none object-contain drop-shadow-[0_15px_22px_rgba(0,0,0,0.2)] transform hover:scale-[1.03] transition-transform duration-300 translate-x-[5%]"
              />
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  )
}
